/**
 * Reset Password Drawer
 *
 * Allows logged-in users to reset their password.
 */

import React, { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button, InputField } from 'equal-ds-ui';
import { KeyRound, Lightbulb } from 'lucide-react';
import { authAPI } from '../../services/auth-api.service';

export interface ResetPasswordDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const DEFAULT_FORM: FormState = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

// Password validation regex
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export const ResetPasswordDrawer: React.FC<ResetPasswordDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user email from session storage
  const getUserEmail = (): string => {
    try {
      const encryptedData = sessionStorage.getItem('loggedUserInfo');
      if (!encryptedData) return '';
      const userInfo = JSON.parse(atob(encryptedData));
      return userInfo.email || '';
    } catch (error) {
      console.error('Error getting user email:', error);
      return '';
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form when drawer opens
      setFormState(DEFAULT_FORM);
      setErrors({});
      setSubmitError(null);
      setSubmitSuccess(null);
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};

    // Old password validation
    if (!formState.oldPassword.trim()) {
      nextErrors.oldPassword = 'Current password is required.';
    }

    // New password validation
    if (!formState.newPassword.trim()) {
      nextErrors.newPassword = 'New password is required.';
    } else if (!PASSWORD_REGEX.test(formState.newPassword)) {
      nextErrors.newPassword = 'Password must contain at least 8 characters, including uppercase, lowercase, number, and special character.';
    } else if (formState.newPassword === formState.oldPassword) {
      nextErrors.newPassword = 'New password must be different from current password.';
    }

    // Confirm password validation
    if (!formState.confirmPassword.trim()) {
      nextErrors.confirmPassword = 'Please confirm your new password.';
    } else if (formState.confirmPassword !== formState.newPassword) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!validate()) {
      return;
    }

    const email = getUserEmail();
    if (!email) {
      setSubmitError('Unable to retrieve user email. Please try logging in again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authAPI.resetPassword({
        email,
        oldpassword: formState.oldPassword,
        newpassword: formState.newPassword,
      });

      if (response.data.status === 'success') {
        setSubmitSuccess('Password reset successfully. You will be redirected to login.');
        setFormState(DEFAULT_FORM);

        // Clear session and redirect to login after 2 seconds
        setTimeout(() => {
          sessionStorage.removeItem('finpro_k_Authorization');
          sessionStorage.removeItem('loggedUserInfo');
          window.location.href = '/site';
        }, 2000);

        onSuccess?.();
      } else {
        setSubmitError(response.data.message || 'Failed to reset password.');
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unable to reset password. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="520px" className="bg-gradient-to-b from-white to-[#F2F8FB]">
        <DrawerHeader
          title="RESET YOUR PASSWORD"
          subtitle="Please enter your current and new desired password to reset your login password"
          icon={<KeyRound className="w-5 h-5" />}
          onClose={onClose}
        />
        <DrawerContent className="p-6 space-y-6 overflow-y-auto flex-1">
          <InputField
            label="Current Password"
            type="password"
            value={formState.oldPassword}
            onChange={(event) => updateField('oldPassword', event.target.value)}
            disabled={isSubmitting}
            error={errors.oldPassword}
            placeholder="Contains at least 1 number and 1 special character"
            required
            autoComplete="current-password"
            helperText="Contains at least 1 number and 1 special character"
          />

          <InputField
            label="New Password"
            type="password"
            value={formState.newPassword}
            onChange={(event) => updateField('newPassword', event.target.value)}
            disabled={isSubmitting}
            error={errors.newPassword}
            placeholder="Enter your new password"
            required
            autoComplete="new-password"
          />

          <InputField
            label="Re-enter New Password"
            type="password"
            value={formState.confirmPassword}
            onChange={(event) => updateField('confirmPassword', event.target.value)}
            disabled={isSubmitting}
            error={errors.confirmPassword}
            placeholder="Re-enter your new password"
            required
            autoComplete="new-password"
          />

          {/* Tips for a strong password */}
          <div className="bg-white rounded-lg p-4 border border-border-default">
            <div className="flex items-start gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <h3 className="text-sm font-semibold text-text-primary">
                Tips for a strong password
              </h3>
            </div>
            <ol className="space-y-2 text-sm text-text-primary ml-7">
              <li>1. Ensure password is at least 8 characters long (12+ for max security)</li>
              <li>2. Password contains at least 1 uppercase letter (A-Z)</li>
              <li>3. Password contains at least 1 lowercase letter (a-z)</li>
              <li>4. Password contains at least 1 number (0-9)</li>
              <li>5. Password contains at least 1 special character ( ! @ # $ % ^ & * ( ) _ - + = &#123; &#125; [ ] ; : " ' &lt; &gt; , . ? / \ | )</li>
            </ol>
          </div>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {submitSuccess}
            </div>
          )}
        </DrawerContent>
        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-3 bg-background-secondary flex-shrink-0">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};
